/**
 * Follow-ups — view chips, search, status filter and sort (redesign Phase 0, 2026-10-02).
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * Works on the row view-models rowModel.js builds (`item.fu` is resolveFollowupState()'s result,
 * plus `statusLabel`, `searchText` and `sort`). Anything else can be filtered too by passing
 * `ctx.stateOf(item)` — the one thing every function here needs is the item's follow-up state.
 *
 * View chips (the export's VIEWS), over the states followupState.js resolves:
 *   all       everything
 *   now       due today or past due — the call list
 *   upcoming  a date after today
 *   notset    no date: 'notset', plus legacy's blank-date button ('nodate')
 *   closed    "Stopped or done": stopped, done, and 'resolved' (appointment scheduled / claim
 *             received — the legacy cell draws nothing for those, which is "nothing left to do")
 * A row that is saving stays in whatever chip it was in only through `ctx.previousState`; without
 * it, a saving row is shown under 'all' only, so it never flickers into the wrong chip.
 */

export const VIEWS = [
  { key: 'all', label: 'All' },
  { key: 'now', label: 'Due now' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'notset', label: 'Not set' },
  { key: 'closed', label: 'Stopped or done' },
];

const VIEW_STATES = {
  now: ['due', 'past'],
  upcoming: ['upcoming'],
  notset: ['notset', 'nodate'],
  closed: ['stopped', 'done', 'resolved'],
};

/** Sort rank of each follow-up state: past → due → upcoming → not set → done → stopped. */
export const FOLLOWUP_SORT_RANK = {
  past: 0, due: 1, upcoming: 2, nodate: 3, notset: 4, done: 5, stopped: 6, resolved: 7, saving: 8,
};

function stateOf(item, ctx) {
  if (ctx && typeof ctx.stateOf === 'function') return ctx.stateOf(item);
  return item && item.fu ? item.fu.state : undefined;
}

export function inView(item, view, ctx) {
  if (!view || view === 'all') return true;
  let s = stateOf(item, ctx);
  if (s === 'saving' && ctx && ctx.previousState) s = ctx.previousState(item);
  const allowed = VIEW_STATES[view];
  return !!allowed && allowed.indexOf(s) !== -1;
}

/** { all, now, upcoming, notset, closed } — the counts on the chips. */
export function countsByView(items, ctx) {
  const out = {};
  VIEWS.forEach((v) => { out[v.key] = 0; });
  (items || []).forEach((it) => {
    VIEWS.forEach((v) => { if (inView(it, v.key, ctx)) out[v.key] += 1; });
  });
  return out;
}

/** Case-insensitive substring match over the row's visible text. Blank query matches everything. */
export function matchesSearch(item, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  return String((item && item.searchText) || '').toLowerCase().indexOf(q) !== -1;
}

/** The status multi-filter: none picked ⇒ everything; otherwise the exact label must be picked. */
export function matchesStatuses(item, statuses) {
  if (!Array.isArray(statuses) || !statuses.length) return true;
  return statuses.indexOf(item && item.statusLabel) !== -1;
}

/** The labels present in the data, sorted, for the status filter's options. */
export function statusOptions(items) {
  const seen = {};
  (items || []).forEach((it) => { if (it && it.statusLabel) seen[it.statusLabel] = true; });
  return Object.keys(seen).sort((a, b) => a.localeCompare(b));
}

/**
 * The value a column sorts by. 'fu' sorts by state rank, then date (earliest first) — so the
 * call list leads with the most overdue. Everything else comes from the view-model's `sort` map;
 * blanks sort last ('~' for text, as the export does for hygienist/carrier).
 */
export function sortValue(item, key, ctx) {
  if (key === 'fu') {
    const rank = FOLLOWUP_SORT_RANK[stateOf(item, ctx)];
    const date = item && item.fu && item.fu.date ? item.fu.date : '';
    return [rank === undefined ? 99 : rank, date];
  }
  const v = item && item.sort ? item.sort[key] : undefined;
  return v === undefined || v === null ? '' : v;
}

function cmp(x, y) {
  if (Array.isArray(x) && Array.isArray(y)) {
    for (let i = 0; i < Math.max(x.length, y.length); i += 1) {
      const c = cmp(x[i], y[i]);
      if (c) return c;
    }
    return 0;
  }
  if (typeof x === 'number' && typeof y === 'number') return x - y;
  const a = String(x);
  const b = String(y);
  return a < b ? -1 : a > b ? 1 : 0;
}

/** A comparator for Array#sort. `dir` 1 = ascending, -1 = descending. Stable ties keep input order. */
export function comparator(key, dir, ctx) {
  const d = dir === -1 ? -1 : 1;
  return (a, b) => {
    const c = cmp(sortValue(a, key, ctx), sortValue(b, key, ctx));
    return c < 0 ? -d : c > 0 ? d : 0;
  };
}

/**
 * The rows the table shows: view chip → status filter → search → sort. Never mutates `items`.
 * @param {Array}  items
 * @param {Object} q  { view, statuses, search, sort: { key, dir } | null }
 */
export function visibleRows(items, q, ctx) {
  const o = q || {};
  let v = (items || []).filter(it => inView(it, o.view, ctx)
    && matchesStatuses(it, o.statuses)
    && matchesSearch(it, o.search));
  if (o.sort && o.sort.key) v = v.slice().sort(comparator(o.sort.key, o.sort.dir, ctx));
  return v;
}
